import { Link } from "react-router-dom";
import { Store, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Introduction",
    body: `ShopOwnaa ("we", "our", or "us") operates the ShopOwnaa Profiler mobile and web application (the "App"). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our App. By using the App you agree to the practices described in this policy.`,
  },
  {
    title: "2. Information We Collect",
    items: [
      {
        label: "Personal Information",
        detail:
          "Full name, email address, phone number, and password collected during account registration.",
      },
      {
        label: "Profile & Store Information",
        detail:
          "Shop name, store address, WhatsApp and Telegram handles, and profile pictures provided when setting up a seller profile.",
      },
      {
        label: "Property & Tenancy Information",
        detail:
          "Data relating to your leases, rental agreements, tenancy IDs, and payment history provided by property managers.",
      },
      {
        label: "Media & Files",
        detail:
          "Images and photos you choose to upload from your device's gallery for product listings or profile avatars.",
      },
      {
        label: "Financial & Transaction Data",
        detail:
          "Payment verification references and transaction identifiers used to confirm plan subscriptions and marketplace payments. We do not store raw card details on our servers.",
      },
    ],
  },
  {
    title: "3. How We Use Your Information",
    items: [
      { detail: "To create and manage your user account." },
      {
        detail:
          "To facilitate tenant services, such as viewing leasing agreements and verifying rent payments.",
      },
      {
        detail:
          "To enable marketplace features, allowing sellers to list products and connect with buyers via WhatsApp or Telegram.",
      },
      {
        detail:
          "To process and verify marketplace plan subscription payments.",
      },
      {
        detail:
          "To provide customer support and send important system notifications.",
      },
    ],
  },
  {
    title: "4. Device Permissions",
    body: `Because the App allows users to upload product images and profile pictures, we request access to your device's photo gallery or storage. This access is used solely to allow you to select and upload images you explicitly choose. We do not access, read, or transmit any files outside of what you deliberately upload.`,
  },
  {
    title: "5. Data Sharing and Disclosure",
    items: [
      {
        label: "Property Managers",
        detail:
          "Tenancy data is shared with the relevant property manager or landlord to facilitate your lease agreement.",
      },
      {
        label: "Service Providers",
        detail:
          "We may share data with secure third-party payment processors (e.g., Paystack) to verify your transactions.",
      },
      {
        label: "Other Users",
        detail:
          "If you are a seller, your shop name, store address, and contact channels (WhatsApp/Telegram) are visible to buyers on the platform.",
      },
      {
        label: "Legal Obligations",
        detail:
          "We may disclose your information if required to do so by law or in response to valid legal requests.",
      },
    ],
  },
  {
    title: "6. Data Retention & Account Deletion",
    body: `You have the right to request deletion of your personal data at any time.

You can delete your account by navigating to Profile → Settings → Delete Account within the App, or by contacting us at the email address listed in Section 8.

Upon account deletion, personal data such as your name, contact information, and product listings will be removed from our active databases within 30 days. Anonymised records may be retained for legal compliance purposes only.`,
  },
  {
    title: "7. Data Security",
    body: `We implement industry-standard security measures including authentication tokens, encrypted HTTPS transport for all API communication, and access-controlled servers to protect your data from unauthorised access, alteration, disclosure, or destruction.`,
  },
  {
    title: "8. Children's Privacy",
    body: `Our App is not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us so we can remove it promptly.`,
  },
  {
    title: "9. Changes to This Policy",
    body: `We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last Updated" date at the top of this page. Continued use of the App after changes are posted constitutes your acceptance of the revised policy.`,
  },
  {
    title: "10. Contact Us",
    body: `If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:\n\nEmail: support@uleval.com\nWebsite: https://api.pmsdemo.uleval.com`,
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-gray-700">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Store size={16} className="text-white" />
            </div>
            <span className="text-sm font-bold text-gray-900">ShopOwnaa</span>
          </Link>
          <span className="text-xs text-gray-400">Privacy Policy</span>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-violet-50 to-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-12 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-violet-100 border border-violet-200 flex items-center justify-center flex-shrink-0 mt-1">
            <ShieldCheck size={22} className="text-violet-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <p className="text-gray-400 mt-2 text-sm">
              Last updated: April 29, 2026
            </p>
            <p className="text-gray-600 mt-3 text-sm leading-relaxed max-w-2xl">
              This policy describes how ShopOwnaa collects, uses, and protects
              the personal information of users of the ShopOwnaa Profiler
              application — covering both the Tenant Portal and the Seller
              Marketplace.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        {SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {section.title}
            </h2>

            {section.body && (
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {section.body}
              </p>
            )}

            {section.items && (
              <ul className="space-y-3 mt-2">
                {section.items.map((item, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
                    <span className="text-gray-600">
                      {item.label && (
                        <span className="font-medium text-gray-800">
                          {item.label}:{" "}
                        </span>
                      )}
                      {item.detail}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <div className="border-t border-gray-200 pt-8 text-xs text-gray-400 text-center">
          © 2026 ShopOwnaa / Uleval. All rights reserved.
        </div>
      </main>
    </div>
  );
}
